// Pages, generated from React Router's own route tree — routes.ts is the only place a
// page is declared. Feeds /sitemap.xml (search engines) and /sitemap (humans).
//
// Every route that renders a component at a concrete path (no `:param` or `*`) is a
// page, and /sitemap (humans) lists ALL of them. Redirects, resource routes, proxies
// and the `:locale` layout render nothing, so they drop out without being marked.
//
// /sitemap.xml lists only the `indexed` subset: pages not under XML_EXCLUDED_PREFIXES.
// Dev tools live under /dev for that reason — put a new one there and it is listed on
// /sitemap but never submitted to search engines.
//
// A page whose humanized path reads badly can override its title:
//
//   export const handle = { sitemap: { title: "Hum by Verizon" } };

import { NON_ENGLISH_LOCALES } from "~/lib/locales";

export const LOCALES = NON_ENGLISH_LOCALES;

export interface SitePage {
  path: string;
  title: string;
  /** True when the `:locale` layout also registers this path for each of LOCALES. */
  localized?: boolean;
  /** Whether the page belongs in /sitemap.xml; the human /sitemap ignores this. */
  indexed: boolean;
}

// Never in /sitemap.xml. /dev holds internal tooling; /ncni mirrors bouncieweb, which
// kept it ("no-crawl/no-index") out of search. Any future page under either inherits this.
// The legal pages stay out of search like bouncieweb's did (robots Disallow + no sitemap).
export const XML_EXCLUDED_PREFIXES = ["/dev", "/ncni", "/privacy-policy", "/terms-of-service", "/terms-of-use"];

/** The part of a route entry that carries its position in the tree. Server build routes
 * and the client manifest both have it, so one path walk serves both. */
export interface RouteShape {
  id: string;
  parentId?: string;
  path?: string;
  index?: boolean;
}

/** The subset of a ServerBuild route entry the generator reads. */
export interface BuildRoute extends RouteShape {
  module: { default?: unknown; handle?: unknown; loader?: unknown };
}

type SitemapHandle = { sitemap?: { title?: string } };

const LOCALE_PARAM = ":locale";

function humanize(segment: string): string {
  return segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Joins the path segments of a route and its ancestors. Pathless layouts and index
// routes contribute nothing, so an index under `:locale` resolves to `/:locale`.
function fullSegments(route: RouteShape, routes: Record<string, RouteShape | undefined>): string[] {
  const parent = route.parentId ? routes[route.parentId] : undefined;
  const inherited = parent ? fullSegments(parent, routes) : [];
  const own = route.path ? route.path.split("/").filter(Boolean) : [];
  return [...inherited, ...own];
}

// Paths the `:locale` layout serves, e.g. "/" and "/products/drive" — i.e. the pages
// that exist in every non-English locale. Derived from the route tree, so registering
// a page under `:locale` (routes.ts does this for every page not marked English-only)
// is the only thing that makes it localized.
export function getLocalizedPaths(routes: Record<string, RouteShape | undefined>): Set<string> {
  const localized = new Set<string>();
  for (const route of Object.values(routes)) {
    if (!route || (!route.index && !route.path)) continue;
    const [first, ...rest] = fullSegments(route, routes);
    if (first === LOCALE_PARAM) localized.add(`/${rest.join("/")}`);
  }
  return localized;
}

export function buildSitePages(routes: Record<string, BuildRoute>): SitePage[] {
  const entries = Object.values(routes).filter((r) => r.index || r.path);
  const localizedPaths = getLocalizedPaths(routes);

  const pages = new Map<string, SitePage>();
  for (const route of entries) {
    const segments = fullSegments(route, routes);
    const handle = route.module.handle as SitemapHandle | undefined;
    const isConcrete = segments.every((s) => !s.startsWith(":") && !s.includes("*"));
    if (!route.module.default || !isConcrete) continue;

    const path = `/${segments.join("/")}`;
    if (pages.has(path)) continue;
    const excludedPrefix = XML_EXCLUDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    pages.set(path, {
      path,
      title: handle?.sitemap?.title ?? (segments.length ? humanize(segments[segments.length - 1]) : "Home"),
      indexed: !excludedPrefix,
      ...(localizedPaths.has(path) && { localized: true }),
    });
  }
  return [...pages.values()];
}

// Routes that serve something other than a page: redirects (registered with an id starting
// `retired-` or `redirect-`, see routes.ts) and resource routes (a loader, no component, e.g.
// robots.txt). The legacy-parity classifier uses these to tell what the new site already answers.
// Wildcard and `:param` paths are skipped: a catch-all proxy is not the same URL as the old one.
export interface NonPagePaths {
  redirects: string[];
  resources: string[];
}

export function buildNonPagePaths(routes: Record<string, BuildRoute>): NonPagePaths {
  const out: NonPagePaths = { redirects: [], resources: [] };
  for (const route of Object.values(routes)) {
    if (!route.path || route.module.default || !route.module.loader) continue;
    const segments = fullSegments(route, routes);
    if (segments.some((seg) => seg.startsWith(":") || seg.includes("*"))) continue;
    const path = `/${segments.join("/")}`;
    (/^(retired|redirect)-/.test(route.id) ? out.redirects : out.resources).push(path);
  }
  out.redirects.sort();
  out.resources.sort();
  return out;
}

// The locale layout's index route becomes /fr-CA, not /fr-CA/ — only prefix, don't join.
export function localizedPath(path: string, locale?: string): string {
  if (!locale) return path;
  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

export interface SiteTreeNode {
  path: string;
  title: string;
  /** Absent for a label-only node whose path has no page of its own (e.g. /products). */
  page?: SitePage;
  children: SiteTreeNode[];
}

export function buildSiteTree(pages: SitePage[]): SiteTreeNode {
  const root: SiteTreeNode = { path: "/", title: "Home", children: [] };
  for (const page of pages) {
    if (page.path === "/") {
      root.page = page;
      root.title = page.title;
      continue;
    }
    let node = root;
    let path = "";
    for (const segment of page.path.split("/").filter(Boolean)) {
      path += `/${segment}`;
      let next = node.children.find((c) => c.path === path);
      if (!next) {
        next = { path, title: humanize(segment), children: [] };
        node.children.push(next);
      }
      node = next;
    }
    node.page = page;
    node.title = page.title;
  }
  return root;
}
